import { DataComponent, NewDataComponent } from "../data/interface.ts"
import { format_function_input_value_string } from "./format_function.ts"
import { EvaluationRequest, EvaluationResponse } from "./interface.ts"
import { load_dependencies_into_runtime } from "./load_dependencies_into_runtime.ts"


interface CalculateResultValueArgs
{
    component: DataComponent | NewDataComponent
    data_components_by_id_and_version: Record<string, DataComponent>
    convert_tiptap_to_javascript: (tiptap_text: string) => string,
    /**
     * When run on the edge functions we pass undefined here as we can't
     * yet evaluate code in a sandbox on the edge functions.
     */
    evaluate_code_in_runtime: undefined | ((request: EvaluationRequest) => Promise<EvaluationResponse>),
    timeout_ms?: number
    debugging?: boolean
}

export async function calculate_result_value(args: CalculateResultValueArgs): Promise<EvaluationResponse | null>
{
    const { component, debugging } = args
    const { input_value } = component

    if (!input_value) return Promise.resolve(null)

    if (args.evaluate_code_in_runtime)
    {
        const load_dependencies_response = await load_dependencies_into_runtime({
            component,
            data_components_by_id_and_version: args.data_components_by_id_and_version,
            evaluate_code_in_runtime: args.evaluate_code_in_runtime,
            debugging,
        })
        if (load_dependencies_response.error) return load_dependencies_response
    }


    const js_input_value = args.convert_tiptap_to_javascript(input_value)
    const basic_request: EvaluationRequest = {
        js_input_value,
        requested_at: performance.now(),
        timeout_ms: args.timeout_ms,
        debugging,
    }

    if (component.value_type === "function")
    {
        const result = format_function_input_value_string({
            ...basic_request,
            function_arguments: component.function_arguments || [],
        })

        return {
            result,
            error: null,

            // TODO: remove these fields from the EvaluationResponse interface
            evaluation_id: 0,
            js_input_value: basic_request.js_input_value,
            requested_at: Date.now(),
            start_time: Date.now(),
            end_time: 0,
        }
    }


    // When on edge function `args.evaluate_code_in_runtime` will be undefined so
    // we just return the result value as the args.component.result_value that
    // was given by the user.
    if (!args.evaluate_code_in_runtime) return Promise.resolve({
        result: args.component.result_value || "",

        evaluation_id: 0,
        js_input_value,
        requested_at: basic_request.requested_at,
        start_time: 0,
        end_time: 0,
        error: null,
    })

    return await args.evaluate_code_in_runtime(basic_request)
}
